import { catchError, map } from 'rxjs/operators';
import { Injectable, Inject } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { DataService } from '../../../core/service/base/data.service';
import { AutoCompleteSelectionItem, IAutoCompleteService } from '../../../core/component/autocomplete-remote/autocomplete-remote.component';
import { AutoCompleteSelectionState } from '../../../core/component/autocomplete-remote/autocomplete-service.interface';
import { APP_CONFIG, IAppConfig } from './../../../app.config';
import { Article } from './../../model/article';
import { LeaseAgreementCategory, RentalObjectCategory } from './../../model/const/enums';
import { Observable } from 'rxjs';
import { ProgressBarService } from '../../../core/service/progress-bar.service';
import { MessageService } from '../../../core/service/message.service';

@Injectable()
export class ArticleService extends DataService<Article> implements IAutoCompleteService<Article> {
  constructor(client: HttpClient, @Inject(APP_CONFIG) config: IAppConfig, messageService: MessageService, progressBar: ProgressBarService) {
    super(client, config, messageService, Article, progressBar, 'article');
  }

  createAutoCompleteSelectionItem(data: Article): AutoCompleteSelectionItem<Article> {
    if (!data) {
      return new AutoCompleteSelectionItem('', null, null);
    }
    const name = `${data.code} ${data.caption}`;
    const shortName = data.code;
    return new AutoCompleteSelectionItem(name, data.id, Object.assign(new Article(), data), shortName);
  }

  getSelection(filter: string, options?: any, page?: number, maxItems?: number): Observable<AutoCompleteSelectionItem<Article>[]> {
    const queryArgs = {
      filter: filter,
      maxItems: maxItems || 20,
    };
    if (options) queryArgs['options'] = options;
    if (page > 0) queryArgs['page'] = page;

    return this.client.post<Article[]>(`${this.endpoint}/selection`, queryArgs).pipe(
      map(data => data.map(item => this.createAutoCompleteSelectionItem(item))),
      catchError(e => {
        this.notification.showError(e);
        return [];
      })
    );
  }

  getSelectionItem(id: string, state: AutoCompleteSelectionState<Article>): Observable<AutoCompleteSelectionItem<Article>> {
    return this.client.get<Article>(`${this.endpoint}/selectionItem/${id}`).pipe(
      map(data => this.createAutoCompleteSelectionItem(data)),
      catchError(e => {
        this.notification.showError(e);
        return [];
      })
    );
  }

  getByCategory(category: LeaseAgreementCategory | string, currencyCode: string): Observable<Article[]> {
    return this.client.get<Article[]>(this.endpoint + `/category/${category}/${currencyCode}`).pipe(
      map(items => {
        if (Array.isArray(items)) {
          return items.map(m => this.createModel(m));
        }
        return [];
      })
    );
  }

  getForRentalChanges(category: RentalObjectCategory): Observable<Article[]> {
    return this.client.get<Article[]>(`${this.endpoint}/rental-change-articles/`, { params: { category: category.toString() } });
  }

  export(ids: string[]): Observable<File> {
    return super.exportDataItems(ids, 'Artiklar.xlsx');
  }

  getFilterDefinition(): Observable<any> {
    return this.client.get<any>(`${this.endpoint}/filter`).pipe(
      map(res => ({
        captionContains: res.captionContains || '',
        code: res.code || '',
        adjustment: res.adjustment || '',

        selectedFields: Array.isArray(res.selectedFields) ? res.selectedFields : ['captionContains'],
        defaultFields: ['captionContains'],
      })),
      catchError(error => {
        console.log(error);
        return [];
      })
    );
  }
}
